import { Injectable } from '@angular/core';
import {
	addressString,
	getRegionName,
	getRegionType,
} from '@core/util/address';
import {
	clientLegalName,
	getSignatories,
	isGuarantor,
} from '@core/util/client';
import { formatCentsToDollars } from '@core/util/money';
import { getPrimaryParcel } from '@core/util/parcels';
import { joinNames, splitSentenceByCharactersLimit } from '@core/util/string';
import { isSet } from '@core/util/value';
import { Business, BusinessAddress, BusinessUser } from '@models/businesses';
import { AddressFormat } from '@models/businessSettings/businessFormatTypes';
import {
	AddressFormattingSettings,
	NameFormattingSettings,
} from '@models/businessSettings/settings';
import {
	Matter,
	PersonMatterClient,
	BusinessMatterClient,
	PersonMatterClientWithAssociations,
	BusinessMatterClientWithAssociations,
	CalculatedLandTransferTax,
	LTTRate,
	MatterTitle,
	ClientsCapacity,
	MatterFileType,
} from '@models/matter';
import { AllAdjustmentsCalculated } from '@state/matter/adjustments/adjustments.selectors';
import { format } from 'date-fns';
import { cloneDeep } from 'lodash';
import { BaseFormMapper } from '../base-form-mapper';
import { Jurisdiction } from '@models/jurisdictions';
import { MatterMortgageWithAssociations } from '@models/mortgage';
import { MapAddress } from '@state/global/tags/mappings/tags.address';
import { getLTTLegalDescription } from '@core/util/legalDescription';
import { getBorrowerServiceAddress } from '@core/util/borrowers';

@Injectable({
	providedIn: 'root',
})
export class LTT_AFFIDAVIT_FORM_Mapper extends BaseFormMapper<LTT_AFFIDAVIT_FORM> {
	CloserToFormMappings = Mappings;

	mergeFormData(
		formData: LTT_AFFIDAVIT_FORM,
		matter: Matter,
		nameFormattingSettings: NameFormattingSettings,
		sellers: (PersonMatterClient | BusinessMatterClient)[],
		buyers: (PersonMatterClient | BusinessMatterClient)[],
		adjustments: AllAdjustmentsCalculated,
		business: Business,
		matterTitles: MatterTitle[],
		matterClients: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		lttCalculated: CalculatedLandTransferTax,
		businessAddress: BusinessAddress,
		otherParties: PersonMatterClient[],
		clientsCapacity: ClientsCapacity,
		mortgage?: MatterMortgageWithAssociations,
		businessUsers?: BusinessUser[],
		primaryJurisdiction?: Jurisdiction,
		signors?: PersonMatterClientWithAssociations[],
		otherPartiesWithAssociations?: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		matterFileType?: MatterFileType,
		addressFormattingSettings?: AddressFormattingSettings,
	): LTT_AFFIDAVIT_FORM {
		const _formData = formData
			? cloneDeep(formData)
			: ({} as LTT_AFFIDAVIT_FORM);

		const FilteredMatterClients =
			matterClients?.filter((client) => !isGuarantor(client)) ?? [];

		const Contacts = getSignatories(FilteredMatterClients);

		const PrimaryTitle = getPrimaryParcel(matterTitles);

		const propertyAddress = MapAddress(
			matter?.propertyDetails?.address,
			addressFormattingSettings || { format: AddressFormat.FORMAT_1 },
		);

		const legalDescription = getLTTLegalDescription(
			matterTitles,
			matter?.propertyDetails?.propertyType?.type,
			primaryJurisdiction,
			propertyAddress,
		);

		const addressOfProperty =
			`${propertyAddress?.lines2?.line1 ?? ''} ${propertyAddress?.lines2?.line2 ?? ''}`.trim();

		const borrowerServiceAddress = getBorrowerServiceAddress(
			matterFileType,
			signors,
			otherPartiesWithAssociations,
			addressFormattingSettings || { format: AddressFormat.FORMAT_1 },
		);

		if (
			(!_formData?.propertyIdentifierNumber1 ||
				_formData?.propertyIdentifierNumber1?.length === 0) &&
			PrimaryTitle
		) {
			_formData.propertyIdentifierNumber1 = PrimaryTitle?.pin?.split('-')[0];
		}

		if (
			(!_formData?.propertyIdentifierNumber2 ||
				_formData?.propertyIdentifierNumber2?.length === 0) &&
			PrimaryTitle
		) {
			_formData.propertyIdentifierNumber2 = PrimaryTitle?.pin?.split('-')[1];
		}

		if (legalDescription) {
			const ConvenyanceOf1CharacterLimit = 70;
			const ConvenyanceOf2And3CharacterLimit = 110;
			const FirstSplittedDescription = splitSentenceByCharactersLimit(
				legalDescription,
				ConvenyanceOf1CharacterLimit,
			);
			const splittedDescription = [FirstSplittedDescription.shift()];
			let SecondSplittedDescription = [];

			if (FirstSplittedDescription.length > 0) {
				SecondSplittedDescription = splitSentenceByCharactersLimit(
					FirstSplittedDescription.join(' '),
					ConvenyanceOf2And3CharacterLimit,
				);
			}

			if (
				(!_formData?.conveyanceOf1 || _formData?.conveyanceOf1?.length === 0) &&
				splittedDescription.length >= 1
			) {
				_formData.conveyanceOf1 = splittedDescription[0];
			}

			if (
				(!_formData?.convenyanceOf2 ||
					_formData?.convenyanceOf2?.length === 0) &&
				SecondSplittedDescription.length >= 1
			) {
				_formData.convenyanceOf2 = SecondSplittedDescription[0];
			}

			if (
				(!_formData?.convenyanceOf3 ||
					_formData?.convenyanceOf3?.length === 0) &&
				SecondSplittedDescription.length > 1
			) {
				_formData.convenyanceOf3 = SecondSplittedDescription[1];
			}
		}

		if (
			(!_formData?.by || _formData?.by?.length === 0) &&
			matterClients?.length > 0
		) {
			_formData.by = joinNames(
				otherParties.map((client) =>
					clientLegalName(client, nameFormattingSettings, {
						applyFormatting: false,
					}),
				),
			);
		}

		if (
			(!_formData?.name || _formData?.name?.length === 0) &&
			matterClients?.length > 0
		) {
			_formData.name = joinNames(
				Contacts.map((contact) =>
					clientLegalName(contact, nameFormattingSettings, {
						applyFormatting: false,
					}),
				),
			);
		}

		if (
			(!_formData?.to || _formData?.to?.length === 0) &&
			matterClients?.length > 0
		) {
			_formData.to = joinNames(
				Contacts.map((client) =>
					clientLegalName(client, nameFormattingSettings, {
						applyFormatting: false,
					}),
				),
			);
		}

		if (
			((!_formData?.moniesPaid || _formData?.moniesPaid?.length === 0) &&
				!lttCalculated?.tax?.moniesPaid &&
				typeof lttCalculated?.nsp === 'number') ||
			(lttCalculated?.tax?.moniesPaid &&
				typeof lttCalculated?.tax?.moniesPaid === 'number')
		) {
			_formData.moniesPaid = formatCentsToDollars(
				lttCalculated?.tax?.moniesPaid ?? lttCalculated?.nsp,
				false,
			);
		}

		if (
			(!_formData?.assumedMortgage ||
				_formData?.assumedMortgage?.length === 0) &&
			typeof lttCalculated?.tax?.assumedMortgage === 'number'
		) {
			_formData.assumedMortgage = formatCentsToDollars(
				lttCalculated?.tax?.assumedMortgage,
				false,
			);
		}

		if (
			(!_formData?.givenBackToVendor ||
				_formData?.givenBackToVendor?.length === 0) &&
			typeof lttCalculated?.tax?.returnedToVendor === 'number'
		) {
			_formData.givenBackToVendor = formatCentsToDollars(
				lttCalculated?.tax?.returnedToVendor,
				false,
			);
		}

		if (
			(!_formData?.propertyTransferredInExchange ||
				_formData?.propertyTransferredInExchange?.length === 0) &&
			typeof lttCalculated?.tax?.propertyTransferred === 'number'
		) {
			_formData.propertyTransferredInExchange = formatCentsToDollars(
				lttCalculated?.tax?.propertyTransferred,
				false,
			);
		}

		if (
			(!_formData?.otherConsiderationsSubjectToTax ||
				_formData?.otherConsiderationsSubjectToTax?.length === 0) &&
			typeof lttCalculated?.tax?.considerations === 'number'
		) {
			_formData.otherConsiderationsSubjectToTax = formatCentsToDollars(
				lttCalculated?.tax?.considerations,
				false,
			);
		}

		if (
			!_formData?.fairMarketValue ||
			_formData?.fairMarketValue?.length === 0
		) {
			_formData.fairMarketValue = 'NIL';
		}

		if (
			(!_formData?.valueSubject || _formData?.valueSubject?.length === 0) &&
			typeof lttCalculated?.subjectToLtt === 'number'
		) {
			_formData.valueSubject = formatCentsToDollars(
				lttCalculated?.subjectToLtt,
				false,
			);
		}

		if (
			(!_formData?.valueSubject2 || _formData?.valueSubject2?.length === 0) &&
			typeof lttCalculated?.subjectToLtt === 'number'
		) {
			_formData.valueSubject2 = formatCentsToDollars(
				lttCalculated?.subjectToLtt,
				false,
			);
		}

		if (!isSet(_formData?.valueOfChattels)) {
			if (lttCalculated?.tax?.applyChattels) {
				_formData.valueOfChattels = formatCentsToDollars(
					lttCalculated?.tax?.chattelsValue,
					false,
				);
			} else {
				_formData.valueOfChattels = 'NIL';
			}
		}

		if (!isSet(_formData?.otherConsideration)) {
			if (lttCalculated?.tax?.applyChattels) {
				_formData.otherConsideration = formatCentsToDollars(
					lttCalculated?.tax?.chattelsConsiderations,
					false,
				);
			} else {
				_formData.otherConsideration = 'NIL';
			}
		}

		if (
			(!_formData?.totalConsideration ||
				_formData?.totalConsideration?.length === 0) &&
			typeof lttCalculated?.subjectToLtt === 'number'
		) {
			_formData.totalConsideration = formatCentsToDollars(
				lttCalculated?.tax?.applyChattels
					? lttCalculated?.tax?.chattelsValue +
							lttCalculated?.tax?.chattelsConsiderations +
							lttCalculated?.subjectToLtt
					: lttCalculated?.subjectToLtt,
				false,
			);
		}

		if (lttCalculated?.tax?.rate && lttCalculated?.subjectToLtt > 200000000) {
			if (lttCalculated?.tax.rate === LTTRate.COMMERCIAL) {
				if (
					!_formData?.singleFamilyCheckbox1B ||
					_formData?.singleFamilyCheckbox1B?.length === 0
				) {
					_formData.singleFamilyCheckbox1B = 'Co';
				}
			}

			if (lttCalculated?.tax.rate === LTTRate.RESIDENTIAL) {
				if (
					!_formData?.singleFamilyCheckbox2B ||
					_formData?.singleFamilyCheckbox2B?.length === 0
				) {
					_formData.singleFamilyCheckbox2B = 'Res';
				}
			}

			if (
				lttCalculated?.tax.rate === LTTRate.APPORTION &&
				typeof lttCalculated?.tax?.residenceValue === 'number'
			) {
				if (
					!_formData?.singleFamilyCheckbox2B ||
					_formData?.singleFamilyCheckbox2B?.length === 0
				) {
					_formData.singleFamilyCheckbox2B = 'App';
				}

				if (
					!_formData?.singleFamilyCheckbox2AmountB ||
					_formData?.singleFamilyCheckbox2AmountB?.length === 0
				) {
					_formData.singleFamilyCheckbox2AmountB = formatCentsToDollars(
						lttCalculated?.tax?.residenceValue,
						false,
					);
				}
			}
		}

		if (
			(!_formData?.swornBefore || _formData?.swornBefore?.length === 0) &&
			businessAddress?.city
		) {
			_formData.swornBefore = `City of ${businessAddress?.city}`;
		}

		if (
			(!_formData?.swornBefore2 || _formData?.swornBefore2?.length === 0) &&
			businessAddress?.province
		) {
			_formData.swornBefore2 = `${getRegionType(businessAddress?.province, businessAddress?.country)} of ${getRegionName(businessAddress?.province, businessAddress?.country)}`;
		}

		if (matter?.closingDate) {
			const date = new Date(matter?.closingDate);
			if (!_formData?.month || _formData?.month?.length === 0) {
				_formData.month = format(date, 'MMMM');
			}

			if (!_formData?.day || _formData?.day?.length === 0) {
				_formData.day = format(date, 'dd');
			}

			if (!_formData?.year || _formData?.year?.length === 0) {
				_formData.year = format(date, 'yy');
			}
		}

		if (
			!_formData?.natureOfInstrument ||
			_formData?.natureOfInstrument?.length === 0
		) {
			_formData.natureOfInstrument = 'TRANSFER';
		}

		if (addressOfProperty) {
			const splitAddressOfProperty = splitSentenceByCharactersLimit(
				addressOfProperty,
				45,
			);
			if (
				(!_formData?.addressOfProperty ||
					_formData?.addressOfProperty?.length === 0) &&
				splitAddressOfProperty.length >= 1
			) {
				_formData.addressOfProperty = splitAddressOfProperty[0];
			}

			if (
				(!_formData?.addressOfProperty2 ||
					_formData?.addressOfProperty2?.length === 0) &&
				splitAddressOfProperty.length >= 2
			) {
				_formData.addressOfProperty2 = splitAddressOfProperty
					.map((address, index) => {
						if (index !== 0) {
							return address;
						}
					})
					.join(' ');
			}
		}

		if (
			matter?.propertyDetails?.rollNumber &&
			!isSet(_formData?.assessmentRollNumber)
		) {
			_formData.assessmentRollNumber = matter.propertyDetails?.rollNumber;
		}

		if (
			(!_formData?.maillingAddress ||
				_formData?.maillingAddress?.length === 0) &&
			borrowerServiceAddress?.lines2?.line1
		) {
			_formData.maillingAddress = borrowerServiceAddress?.lines2?.line1 ?? '';
		}

		if (
			(!_formData?.maillingAddress2 ||
				_formData?.maillingAddress2?.length === 0) &&
			borrowerServiceAddress?.lines2?.line2
		) {
			_formData.maillingAddress2 = borrowerServiceAddress?.lines2?.line2 ?? '';
		}

		if (
			(!_formData?.nameAndAddressesOfTransfereeSolictor1 ||
				_formData?.nameAndAddressesOfTransfereeSolictor1?.length === 0) &&
			matter?.lawyerAssigned
		) {
			_formData.nameAndAddressesOfTransfereeSolictor1 =
				matter?.lawyerAssigned?.name;
		}

		if (
			(!_formData?.nameAndAddressesOfTransfereeSolictor2 ||
				_formData?.nameAndAddressesOfTransfereeSolictor2?.length === 0) &&
			matter?.number
		) {
			_formData.nameAndAddressesOfTransfereeSolictor2 = `${addressString(businessAddress)} - [File ${matter?.number ?? ''}]`;
		}

		return _formData;
	}

	MapValues(): Partial<LTT_AFFIDAVIT_FORM> {
		return {};
	}
}

const Mappings: LTT_AFFIDAVIT_FORM = {
	propertyIdentifierNumber1: 'Text2',
	propertyIdentifierNumber2: 'Text2aaaa',
	conveyanceOf1: 'Text1',
	convenyanceOf2: 'Text4',
	convenyanceOf3: 'Text5',
	by: 'Text6',
	to: 'Text7',
	name: 'Text412313123123',
	capacity1: 'CC069',
	capacity2: 'Check Box18',
	capacity3: 'Check Box18123123',
	capacity1Authorize: 'Text9',
	capacity1Transferee: 'Text10',
	capacity2Spouse1: 'Text11',
	capacity2Spouse2: 'Text12',
	tenderingForRegistration: 'Check Box45454',
	moniesPaid: 'cc015',
	assumedMortgage: 'cc016',
	givenBackToVendor: 'cc017',
	propertyTransferredInExchange: 'cc018',
	otherConsiderationsSubjectToTax: 'cc019',
	fairMarketValue: 'cc020',
	valueSubject: 'cc021',
	valueSubject2: 'cc022',
	valueOfChattels: 'cc023',
	otherConsideration: 'cc088',
	totalConsideration: 'cc089',
	singleFamilyCheckbox1: 'cc59',
	singleFamilyCheckbox2: 'cc55',
	singleFamilyCheckbox2Amount: 'cc024',
	singleFamilyCheckbox2Purposes: 'CC025',
	singleFamilyCheckbox3: 'cc56',
	singleFamilyCheckbox3AgreementDate: 'CC026',
	singleFamilyCheckbox1B: 'cc60',
	singleFamilyCheckbox2B: 'cc57',
	singleFamilyCheckbox2AmountB: 'CC027',
	singleFamilyCheckbox2PurposesB: 'CC028',
	singleFamilyCheckbox3B: 'cc58',
	singleFamilyCheckbox3AgreementDateB: 'CC029',
	subjectToEncumbrances: 'Check Box75',
	nonResidentSpeculationTax: 'Check Box76',

	nonResidentSpeculationTax1: 'Check Box76a',
	nonResidentSpeculationTax2: 'Check Box76b',
	nonResidentSpeculationTax3: 'Check Box76c',
	nonResidentSpeculationTax4: 'Check Box76d',
	nonResidentSpeculationTax5: 'Check Box76e',
	nonResidentSpeculationTax6: 'Check Box76f',

	nonResidentSpeculationTaxAmount: 'Text30',
	nonResidentSpeculationTaxPurposes: 'Text31',
	notSubjectToAdditionalTaxCheckbox1: 'Check Box55',
	notSubjectToAdditionalTaxCheckbox2: 'Check Box56',
	notSubjectToAdditionalTaxCheckbox3: 'Check Box57',
	notSubjectToAdditionalTaxCheckbox4: 'Check Box58',
	notSubjectToAdditionalTaxCheckbox5: 'Check Box59',
	notSubjectToAdditionalTaxCheckbox6: 'Check Box60',
	notSubjectToAdditionalTaxCheckbox7: 'Check Box61',
	notSubjectToAdditionalTaxCheckbox8: 'Check Box62',
	notSubjectToAdditionalTaxCheckbox8Reason: 'Text32',
	paragraph6AB: 'Check Box54',
	paragraph6BName: 'Text33',
	paragraph6C: 'Check Box53',
	affidavitCompletedBySolicitorCheckbox: 'CC077',
	affidavitCompletedBySolicitorNames: 'CC034',
	isRequiredToBeProvidedForConveyance: 'Check Box63',
	otherRemarks: 'Text35',
	otherRemarks2: 'Text36',
	swornBefore: 'Text37',
	swornBefore2: 'Text38',
	day: 'Text39',
	month: 'Text40',
	year: 'Text41',
	natureOfInstrument: 'CC044',
	addressOfProperty: 'Text45',
	addressOfProperty2: 'Text46',
	assessmentRollNumber: 'Text47',
	maillingAddress: 'Text48',
	maillingAddress2: 'Text49',
	registrationNumber: 'Text50',
	legalDescriptionOfProperty: 'Check Box64',
	nameAndAddressesOfTransfereeSolictor1: 'Text51',
	nameAndAddressesOfTransfereeSolictor2: 'Text52',
	schoolSupportA: 'Check Box65',
	schoolSupportB: 'Check Box66',
	schoolSupportC: 'Check Box67',
	schoolSupportD: 'Check Box68',
};
export interface LTT_AFFIDAVIT_FORM {
	propertyIdentifierNumber1: string;
	propertyIdentifierNumber2: string;
	conveyanceOf1: string;
	convenyanceOf2: string;
	convenyanceOf3: string;
	by: string;
	to: string;
	name: string;
	capacity1: string;
	capacity2: string;
	capacity3: string;
	capacity1Authorize: string;
	capacity1Transferee: string;
	capacity2Spouse1: string;
	capacity2Spouse2: string;
	tenderingForRegistration: string;
	moniesPaid: string;
	assumedMortgage: string;
	givenBackToVendor: string;
	propertyTransferredInExchange: string;
	otherConsiderationsSubjectToTax: string;
	fairMarketValue: string;
	valueSubject: string;
	valueSubject2: string;
	valueOfChattels: string;
	otherConsideration: string;
	totalConsideration: string;
	singleFamilyCheckbox1: string;
	singleFamilyCheckbox2: string;
	singleFamilyCheckbox2Amount: string;
	singleFamilyCheckbox2Purposes: string;
	singleFamilyCheckbox3: string;
	singleFamilyCheckbox3AgreementDate: string;
	singleFamilyCheckbox1B: string;
	singleFamilyCheckbox2B: string;
	singleFamilyCheckbox2AmountB: string;
	singleFamilyCheckbox2PurposesB: string;
	singleFamilyCheckbox3B: string;
	singleFamilyCheckbox3AgreementDateB: string;
	subjectToEncumbrances: string;
	nonResidentSpeculationTax: string;

	nonResidentSpeculationTax1: string;
	nonResidentSpeculationTax2: string;
	nonResidentSpeculationTax3: string;

	nonResidentSpeculationTax4: string;
	nonResidentSpeculationTax5: string;
	nonResidentSpeculationTax6: string;

	nonResidentSpeculationTaxAmount: string;
	nonResidentSpeculationTaxPurposes: string;
	notSubjectToAdditionalTaxCheckbox1: string;
	notSubjectToAdditionalTaxCheckbox2: string;
	notSubjectToAdditionalTaxCheckbox3: string;
	notSubjectToAdditionalTaxCheckbox4: string;
	notSubjectToAdditionalTaxCheckbox5: string;
	notSubjectToAdditionalTaxCheckbox6: string;
	notSubjectToAdditionalTaxCheckbox7: string;
	notSubjectToAdditionalTaxCheckbox8: string;
	notSubjectToAdditionalTaxCheckbox8Reason: string;
	paragraph6AB: string;
	paragraph6BName: string;
	paragraph6C: string;
	affidavitCompletedBySolicitorCheckbox: string;
	affidavitCompletedBySolicitorNames: string;
	isRequiredToBeProvidedForConveyance: string;
	otherRemarks: string;
	otherRemarks2: string;
	swornBefore: string;
	swornBefore2: string;
	day: string;
	month: string;
	year: string;

	natureOfInstrument: string;
	addressOfProperty: string;
	addressOfProperty2: string;
	assessmentRollNumber: string;
	maillingAddress: string;
	maillingAddress2: string;
	registrationNumber: string;
	legalDescriptionOfProperty: string;
	nameAndAddressesOfTransfereeSolictor1: string;
	nameAndAddressesOfTransfereeSolictor2: string;

	schoolSupportA: string;
	schoolSupportB: string;
	schoolSupportC: string;
	schoolSupportD: string;
}
